import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import verifiedBadge from "../../assets/verified.png";
import Avatar from "../../componentes/ui/Avatar";
import Badge from "../../componentes/ui/Badge";
import Card from "../../componentes/ui/Card";
import { useCommunityAuth } from "../../contexts/CommunityAuthContext";
import { formatNumber } from "../../utils/numberFormat";
import { Drawer, Pagination, message } from "antd";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import LoginPrompt from "../../components/community/shared/LoginPrompt";
import {
  Mail,
  Phone,
  ArrowLeft,
  Award,
  Heart,
  MessageSquare,
  Eye,
  UserCircle,
  ShieldCheck,
  ShieldAlert,
  Ban,
  MegaphoneOff,
  MessageSquareOff,
  Users,
  UserPlus,
  Check,
  Plus,
} from "lucide-react";

export default function PublicProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user: viewer, loading: authLoading } = useCommunityAuth();
  const isProfileAdmin = (profile?.role || "").toLowerCase() === "admin";
  const syncFollowStats = useCallback((payload) => {
    if (!payload) {
      setFollowStats({ followers: 0, following: 0, isFollowing: false });
      return;
    }
    setFollowStats({
      followers:
        typeof payload.followers_count === "number"
          ? payload.followers_count
          : 0,
      following:
        typeof payload.following_count === "number"
          ? payload.following_count
          : 0,
      isFollowing: Boolean(payload.is_following),
    });
  }, []);
  const FOLLOW_PAGE_SIZE = 10;
  const [followStats, setFollowStats] = useState({
    followers: 0,
    following: 0,
    isFollowing: false,
  });

  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  };
  const [followLoading, setFollowLoading] = useState(false);
  const [followersDrawerOpen, setFollowersDrawerOpen] = useState(false);
  const [followingDrawerOpen, setFollowingDrawerOpen] = useState(false);
  const [followersData, setFollowersData] = useState([]);
  const [followingData, setFollowingData] = useState([]);
  const [followersTotal, setFollowersTotal] = useState(0);
  const [followingTotal, setFollowingTotal] = useState(0);
  const [followersPage, setFollowersPage] = useState(1);
  const [followingPage, setFollowingPage] = useState(1);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [loginPromptAction, setLoginPromptAction] = useState(null);
  const CONTRIBUTION_PAGE_SIZE = 6;
  const [contributionDrawerOpen, setContributionDrawerOpen] = useState(false);
  const [activeContributionMeta, setActiveContributionMeta] = useState(null);
  const [contributionItems, setContributionItems] = useState([]);
  const [contributionTotal, setContributionTotal] = useState(0);
  const [contributionPage, setContributionPage] = useState(1);
  const [contributionLoading, setContributionLoading] = useState(false);
  const canViewUnapproved = useMemo(() => {
    if (!viewer) {
      return false;
    }
    if (profile?.username && viewer.username === profile.username) {
      return true;
    }
    if (viewer.is_superuser || viewer.is_staff) {
      return true;
    }
    return (viewer.role || "").toLowerCase() === "admin";
  }, [profile?.username, viewer]);
  const filterApprovedItems = useCallback(
    (items) => {
      if (!Array.isArray(items)) {
        return [];
      }
      if (canViewUnapproved) {
        return items;
      }
      return items.filter((entry) => entry?.is_approved !== false);
    },
    [canViewUnapproved]
  );

  useEffect(() => {
    if (!username) return;
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get(
          `/api/community/users/${username}/dashboard/`
        );
        setProfile(res.data);
      } catch (err) {
        console.error("Failed to load profile", err);
        setError("We couldn't load this member's profile right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  useEffect(() => {
    syncFollowStats(profile);
  }, [
    profile?.followers_count,
    profile?.following_count,
    profile?.is_following,
    syncFollowStats,
  ]);

  const contributions = useMemo(() => {
    const summary = profile?.Contributions || {};
    const normalize = (key) => {
      const section = summary[key] || {};
      return {
        count: Number(section.count) || 0,
        recent: filterApprovedItems(section.recent),
      };
    };
    return {
      blog: normalize("blog"),
      forum: normalize("forum"),
      learning: normalize("learning"),
      challenges: normalize("challenges"),
      bookmarks: normalize("bookmarks"),
    };
  }, [filterApprovedItems, profile]);

  const summaryStats = useMemo(
    () => [
      {
        label: "Reputation",
        value: profile?.reputation || 0,
        icon: <Award className="w-5 h-5" />,
        accent: "from-emerald-500/10 via-emerald-500/5 to-transparent",
      },
      {
        label: "Total Likes",
        value: profile?.total_likes || 0,
        icon: <Heart className="w-5 h-5" />,
        accent: "from-rose-500/10 via-rose-500/5 to-transparent",
      },
      {
        label: "Total Comments",
        value: profile?.total_comments || 0,
        icon: <MessageSquare className="w-5 h-5" />,
        accent: "from-sky-500/10 via-sky-500/5 to-transparent",
      },
      {
        label: "Total Views",
        value: profile?.total_views || 0,
        icon: <Eye className="w-5 h-5" />,
        accent: "from-violet-500/10 via-violet-500/5 to-transparent",
      },
    ],
    [profile]
  );

  const contributionSections = useMemo(
    () => [
      {
        type: "blog",
        title: "Blog Articles",
        tone: "from-blue-500/10 via-blue-500/5 to-transparent",
        hint: "Deep dives and thought leadership pieces authored for the blog.",
        items: contributions.blog.recent,
        count: contributions.blog.count,
        empty: "No blog posts yet",
        linkFor: (entry) =>
          entry?.slug
            ? `/blog/${entry.slug}`
            : entry?.id
            ? `/blog/${entry.id}`
            : null,
      },
      {
        type: "forum",
        title: "Forum Conversations",
        tone: "from-emerald-500/10 via-emerald-500/5 to-transparent",
        hint: "Highlighted discussions started to engage the community.",
        items: contributions.forum.recent,
        count: contributions.forum.count,
        empty: "No forum threads yet",
        linkFor: (entry) =>
          entry?.slug
            ? `/forum/${entry.slug}`
            : entry?.id
            ? `/forum/${entry.id}`
            : null,
      },
      {
        type: "learning",
        title: "Learning Paths",
        tone: "from-purple-500/10 via-purple-500/5 to-transparent",
        hint: "Curated journeys guiding learners through key topics.",
        items: contributions.learning.recent,
        count: contributions.learning.count,
        empty: "No learning paths yet",
        linkFor: (entry) =>
          entry?.id ? `/community/learning/${entry.id}` : null,
      },
      // {
      //   type: "challenges",
      //   title: isProfileAdmin ? "Challenges Authored" : "Challenges Solved",
      //   tone: "from-orange-500/10 via-orange-500/5 to-transparent",
      //   hint: isProfileAdmin
      //     ? "Hands-on challenges designed to test real-world skills."
      //     : "Highlighted challenges this member has successfully completed.",
      //   items: contributions.challenges.recent,
      //   count: contributions.challenges.count,
      //   empty: isProfileAdmin
      //     ? "No challenges yet"
      //     : "No solved challenges yet",
      //   linkFor: (entry) => entry?.url || "/ctf",
      // },
    ],
    [contributions, isProfileAdmin]
  );

  const normalizeFollowResponse = useCallback((payload) => {
    if (!payload) {
      return { results: [], total: 0 };
    }
    if (Array.isArray(payload)) {
      return { results: payload, total: payload.length };
    }
    const results = Array.isArray(payload.results) ? payload.results : [];
    const total =
      typeof payload.count === "number" ? payload.count : results.length;
    return { results, total };
  }, []);

  const fetchFollowersList = useCallback(
    async (page = 1) => {
      if (!profile?.username) {
        return;
      }
      setFollowersLoading(true);
      try {
        const { data } = await api.get(
          `/api/community/users/${profile.username}/followers/`,
          {
            params: {
              page,
              page_size: FOLLOW_PAGE_SIZE,
            },
          }
        );
        const { results, total } = normalizeFollowResponse(data);
        setFollowersData(results);
        setFollowersTotal(total);
      } catch (err) {
        console.error("Failed to load followers", err);
        message.error("Unable to load followers right now.");
      } finally {
        setFollowersLoading(false);
      }
    },
    [FOLLOW_PAGE_SIZE, normalizeFollowResponse, profile?.username]
  );

  const fetchFollowingList = useCallback(
    async (page = 1) => {
      if (!profile?.username) {
        return;
      }
      setFollowingLoading(true);
      try {
        const { data } = await api.get(
          `/api/community/users/${profile.username}/following/`,
          {
            params: {
              page,
              page_size: FOLLOW_PAGE_SIZE,
            },
          }
        );
        const { results, total } = normalizeFollowResponse(data);
        setFollowingData(results);
        setFollowingTotal(total);
      } catch (err) {
        console.error("Failed to load following list", err);
        message.error("Unable to load following list right now.");
      } finally {
        setFollowingLoading(false);
      }
    },
    [FOLLOW_PAGE_SIZE, normalizeFollowResponse, profile?.username]
  );

  const handleContributionDrawerOpen = useCallback(
    (section) => {
      if (!section?.type || !profile?.username || !section.count) {
        return;
      }
      setContributionItems([]);
      setContributionTotal(0);
      setContributionPage(1);
      setActiveContributionMeta({
        type: section.type,
        title: section.title,
        hint: section.hint,
        empty: section.empty,
        tone: section.tone,
      });
      setContributionDrawerOpen(true);
    },
    [profile?.username]
  );

  const handleContributionDrawerClose = useCallback(() => {
    setContributionDrawerOpen(false);
    setContributionItems([]);
    setContributionTotal(0);
    setContributionPage(1);
    setActiveContributionMeta(null);
  }, []);

  const fetchContributionsList = useCallback(
    async (page = 1, type) => {
      if (!profile?.username || !type) {
        return;
      }
      setContributionLoading(true);
      try {
        const { data } = await api.get(
          `/api/community/users/${profile.username}/contributions/`,
          {
            params: {
              type,
              page,
              page_size: CONTRIBUTION_PAGE_SIZE,
            },
          }
        );
        const results = Array.isArray(data?.results) ? data.results : [];
        const approvedResults = filterApprovedItems(results);
        const total =
          typeof data?.count === "number" ? data.count : approvedResults.length;
        if (!approvedResults.length && total > 0 && page > 1) {
          setContributionPage((prev) => (prev === page ? page - 1 : prev));
          return;
        }
        setContributionItems(approvedResults);
        setContributionTotal(total);
      } catch (err) {
        console.error("Failed to load contributions list", err);
        message.error("Unable to load contributions right now.");
      } finally {
        setContributionLoading(false);
      }
    },
    [CONTRIBUTION_PAGE_SIZE, filterApprovedItems, profile?.username]
  );

  useEffect(() => {
    if (!followersDrawerOpen) {
      setFollowersPage(1);
      setFollowersData([]);
      return;
    }
    fetchFollowersList(followersPage);
  }, [fetchFollowersList, followersDrawerOpen, followersPage]);

  useEffect(() => {
    if (!followingDrawerOpen) {
      setFollowingPage(1);
      setFollowingData([]);
      return;
    }
    fetchFollowingList(followingPage);
  }, [fetchFollowingList, followingDrawerOpen, followingPage]);

  useEffect(() => {
    if (!contributionDrawerOpen) {
      return;
    }
    const type = activeContributionMeta?.type;
    if (!type) {
      return;
    }
    fetchContributionsList(contributionPage, type);
  }, [
    activeContributionMeta?.type,
    contributionDrawerOpen,
    contributionPage,
    fetchContributionsList,
  ]);

  const handleFollowToggle = useCallback(async () => {
    if (!profile?.username) {
      return;
    }
    if (!viewer) {
      setLoginPromptAction("follow");
      return;
    }
    if (viewer.username === profile.username) {
      message.warning("You cannot follow your own profile.");
      return;
    }
    if (followLoading) {
      return;
    }

    const statusInfo = profile.status || {};
    if (statusInfo.is_banned || statusInfo.currently_suspended) {
      message.warning("Following is not available for this member.");
      return;
    }

    const currentlyFollowing = followStats.isFollowing;
    const action = currentlyFollowing ? "unfollow" : "follow";
    setFollowLoading(true);
    try {
      const { data } = await api.post(
        `/api/community/users/${profile.username}/${action}/`
      );
      syncFollowStats(data);
      setProfile((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          followers_count:
            typeof data?.followers_count === "number"
              ? data.followers_count
              : prev.followers_count,
          following_count:
            typeof data?.following_count === "number"
              ? data.following_count
              : prev.following_count,
          is_following:
            typeof data?.is_following === "boolean"
              ? data.is_following
              : prev.is_following,
        };
      });
      message.success(
        currentlyFollowing
          ? "You are no longer following this member."
          : "You are now following this member."
      );
    } catch (err) {
      console.error("Follow toggle failed", err);
      message.error(
        currentlyFollowing
          ? "Unable to unfollow right now."
          : "Unable to follow this member right now."
      );
    } finally {
      setFollowLoading(false);
    }
  }, [
    followLoading,
    followStats.isFollowing,
    profile,
    syncFollowStats,
    viewer,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-24">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <FullscreenSpinner message="Loading profile..." tone="slate" />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24 px-6 text-center">
        <div className="max-w-md">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
            Profile unavailable
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {error ||
              "This community member hasn't shared their public profile yet."}
          </p>
        </div>
      </div>
    );
  }

  const coverImage = profile.coverImage || profile.cover_url;
  const avatarSrc = profile.avatar || profile.avatar_url;
  const skills = Array.isArray(profile.skills) ? profile.skills : [];
  const activityFeed = Array.isArray(profile.recentActivity)
    ? profile.recentActivity.slice(0, 6)
    : [];
  const isOwnProfile = Boolean(
    viewer && profile && viewer.username === profile.username
  );
  const statusInfo = profile.status || {};
  const statusSummary = profile.status_summary || {};
  const statusBadgeList = Array.isArray(statusSummary.badges)
    ? Array.from(new Set(statusSummary.badges))
    : [];
  const isBanned = Boolean(statusInfo.is_banned);
  const currentlySuspended = Boolean(statusInfo.currently_suspended);
  const postingEnabled =
    !isBanned && !currentlySuspended && statusInfo.can_post !== false;
  const commentingEnabled =
    !isBanned && !currentlySuspended && statusInfo.can_comment !== false;
  const STATUS_PILL_THEMES = {
    positive: {
      wrapper:
        "bg-emerald-500/20 text-emerald-100 border border-emerald-400/50",
      icon: "text-emerald-100",
    },
    warning: {
      wrapper: "bg-amber-500/25 text-amber-100 border border-amber-400/40",
      icon: "text-amber-100",
    },
    danger: {
      wrapper: "bg-rose-500/30 text-rose-100 border border-rose-400/50",
      icon: "text-rose-100",
    },
    info: {
      wrapper: "bg-white/20 text-white border border-white/25",
      icon: "text-white",
    },
  };
  const statusHeroPills = [];
  const pillLabels = new Set();
  const pushPill = (label, variant, IconComponent) => {
    if (!label || pillLabels.has(label)) {
      return;
    }
    pillLabels.add(label);
    statusHeroPills.push({ label, variant, IconComponent });
  };
  if (isBanned) {
    pushPill("Banned Account", "danger", Ban);
  }
  if (!isBanned && currentlySuspended) {
    pushPill("Suspended", "warning", ShieldAlert);
  }
  if (!postingEnabled) {
    pushPill("Posting Restricted", "warning", MegaphoneOff);
  }
  if (!commentingEnabled) {
    pushPill("Commenting Restricted", "warning", MessageSquareOff);
  }
  statusBadgeList.forEach((badge) => pushPill(badge, "positive", ShieldCheck));
  if (!statusHeroPills.length && !isBanned && !currentlySuspended) {
    pushPill("Active Member", "info", ShieldCheck);
  }
  const followRestricted = isBanned || currentlySuspended;

  const formatFollowedAt = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "—";
    }
    return parsed.toLocaleDateString();
  };

  const formatDate = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  };

  const resolveActivityLink = (item) => {
    if (!item) return null;
    if (item.url) return item.url;
    if (item.type === "blog" && item.slug) return `/blog/${item.slug}`;
    if (item.type === "forum" && item.slug) return `/forum/${item.slug}`;
    if (item.type === "challenge") return "/ctf";
    if (item.type === "tutorial" && item.slug)
      return `/community/tutorials/${item.slug}`;
    return null;
  };

  const buildLinkTarget = (link) => {
    if (!link) {
      return null;
    }
    return link.startsWith("/blog/")
      ? { pathname: link, state: { fromPublic: true } }
      : link;
  };

  const resolveContributionLink = (type, item) => {
    if (!type || !item) {
      return null;
    }
    if (item.url) {
      return item.url;
    }
    if (type === "blog") {
      if (item.slug) {
        return `/blog/${item.slug}`;
      }
      if (item.id) {
        return `/blog/${item.id}`;
      }
      if (item.post_type && item.slug) {
        return `/community/${item.post_type}/${item.slug}`;
      }
    }
    if (type === "forum") {
      return item.slug ? `/forum/${item.slug}` : null;
    }
    if (type === "learning") {
      return item.id ? `/community/learning/${item.id}` : null;
    }
    // if (type === "challenges") {
    //   if (item.slug) {
    //     return `/ctf/challenges/${item.slug}`;
    //   }
    //   return "/ctf";
    // }
    return null;
  };

  const renderContributionCard = (item) => {
    const type = activeContributionMeta?.type;
    if (!item || !type) {
      return null;
    }

    if (type === "blog") {
      const likes = formatNumber(item.likes || 0);
      const comments = formatNumber(item.comments || 0);
      const views = formatNumber(item.views || 0);
      return (
        <div className="rounded-2xl border border-gray-200 bg-white/80 p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900/70">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-2">
              <p className="text-base font-semibold text-gray-900 dark:text-white line-clamp-2">
                {item.title}
              </p>
              {item.excerpt && (
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                  {stripHtml(item.excerpt)}
                </p>
              )}
            </div>
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {item.date || "—"}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span>{likes} likes</span>
            <span>{comments} comments</span>
            <span>{views} views</span>
          </div>
          {item.is_approved === false && (
            <span className="mt-3 inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              Pending review
            </span>
          )}
        </div>
      );
    }

    if (type === "forum") {
      const replies = formatNumber(item.replies || 0);
      const views = formatNumber(item.views || 0);
      const solved = item.status === "solved";
      return (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 shadow-sm dark:border-emerald-900/40 dark:bg-emerald-900/20">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-200">
                {item.category || "Forum"}
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white line-clamp-2">
                {item.title}
              </p>
            </div>
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {item.date || "—"}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
            <span>{replies} replies</span>
            <span>{views} views</span>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold ${
                solved
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
                  : "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
              }`}
            >
              {solved ? "Solved" : "Active"}
            </span>
          </div>
          {item.is_approved === false && (
            <span className="mt-3 inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              Awaiting moderation
            </span>
          )}
        </div>
      );
    }

    if (type === "learning") {
      const modules = formatNumber(item.modules || 0);
      const completed = formatNumber(item.completed || 0);
      const progressRaw =
        typeof item.progress === "number" ? Math.round(item.progress) : 0;
      const progressValue = Math.min(Math.max(progressRaw, 0), 100);
      return (
        <div className="rounded-2xl border border-purple-200 bg-purple-50/70 p-5 shadow-sm dark:border-purple-900/40 dark:bg-purple-900/20">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-base font-semibold text-gray-900 dark:text-white line-clamp-2">
                {item.title}
              </p>
              {item.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3">
                  {stripHtml(item.description)}
                </p>
              )}
            </div>
            <span className="text-xs font-semibold text-purple-600 dark:text-purple-200 whitespace-nowrap">
              {progressValue}% complete
            </span>
          </div>
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-purple-100 dark:bg-purple-950/50">
            <div
              className="h-full rounded-full bg-purple-500 dark:bg-purple-300"
              style={{ width: `${progressValue}%` }}
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
            <span>{modules} modules</span>
            <span>{completed} completed</span>
            {item.difficulty && <span>{item.difficulty}</span>}
          </div>
        </div>
      );
    }

    if (type === "challenges") {
      const points = formatNumber(item.points || 0);
      const solved = Boolean(item.completed);
      return (
        <div className="rounded-2xl border border-orange-200 bg-orange-50/70 p-5 shadow-sm dark:border-orange-900/40 dark:bg-orange-900/20">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-base font-semibold text-gray-900 dark:text-white line-clamp-2">
                {item.title}
              </p>
              {item.category && (
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-200">
                  {item.category}
                </p>
              )}
            </div>
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {item.date || "—"}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-700 dark:text-gray-200">
            {item.difficulty && (
              <span className="inline-flex items-center rounded-full bg-white/70 px-2.5 py-0.5 font-semibold text-orange-700 dark:bg-gray-900/60 dark:text-orange-200">
                {item.difficulty}
              </span>
            )}
            {!!item.points && (
              <span className="inline-flex items-center rounded-full bg-white/70 px-2.5 py-0.5 font-semibold text-orange-700 dark:bg-gray-900/60 dark:text-orange-200">
                {points} pts
              </span>
            )}
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold ${
                solved
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
                  : "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
              }`}
            >
              {solved ? "Solved" : "In progress"}
            </span>
          </div>
          {Array.isArray(item.tags) && item.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {item.tags.slice(0, 6).map((tag) => (
                <span
                  key={tag.id || tag.slug || tag.name}
                  className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300"
                >
                  #{tag.name || tag.slug}
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  console.log("Profile data:", profile);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 pt-20 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        <section className="relative overflow-hidden rounded-3xl shadow-2xl">
          <div className="absolute inset-0">
            {coverImage ? (
              <img
                src={coverImage}
                alt="Profile cover"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-indigo-700 via-purple-600 to-sky-500" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/30 to-transparent" />
          </div>

          <div className="relative px-6 sm:px-10 lg:px-14 pt-12 pb-10 lg:pt-16 lg:pb-24 space-y-10">
            <div className="flex flex-col lg:flex-row lg:items-end gap-8">
              <Avatar
                src={avatarSrc}
                name={profile.name || profile.username}
                size={32}
                className="border-4 border-white/90 shadow-2xl"
              />
              <div className="flex-1 text-white">
                <div className="flex flex-wrap items-center gap-3 text-sm text-white/70 mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10">
                    <UserCircle className="w-4 h-4" />@{profile.username}
                  </span>
                  {profile.joinDate && (
                    <span>Member since {profile.joinDate}</span>
                  )}
                  {profile.location && <span>{profile.location}</span>}
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight flex items-center gap-2">
                  {profile.name || profile.username}
                  {(profile.is_trusted || profile.status?.is_trusted) && (
                    <img
                      src={verifiedBadge}
                      alt="Verified"
                      className="w-5 h-5 shrink-0 drop-shadow-sm translate-y-[1px]"
                    />
                  )}
                </h1>
                <p className="text-lg sm:text-xl text-white/80 mt-3 max-w-2xl">
                  {profile.title || "Community Member"}
                </p>
                {statusHeroPills.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {statusHeroPills.map(
                      ({ label, variant, IconComponent }) => {
                        const theme =
                          STATUS_PILL_THEMES[variant] ||
                          STATUS_PILL_THEMES.info;
                        return (
                          <span
                            key={label}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold backdrop-blur-sm ${theme.wrapper}`}
                          >
                            <IconComponent
                              className={`h-3.5 w-3.5 ${theme.icon}`}
                            />
                            {label}
                          </span>
                        );
                      }
                    )}

                    {/* Follow Stats and Action Buttons */}
                    <button
                      type="button"
                      onClick={() => setFollowersDrawerOpen(true)}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold backdrop-blur-sm transition hover:opacity-90 ${STATUS_PILL_THEMES.info.wrapper}`}
                    >
                      <div className="inline-flex items-center gap-2">
                        <Users className="h-3.5 w-3.5" />
                        <span>
                          <span className="font-bold">
                            {formatNumber(followStats.followers)}
                          </span>
                          <span className="ml-1 opacity-80">Followers</span>
                        </span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFollowingDrawerOpen(true)}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold backdrop-blur-sm transition hover:opacity-90 ${STATUS_PILL_THEMES.info.wrapper}`}
                    >
                      <div className="inline-flex items-center gap-2">
                        <UserPlus className="h-3.5 w-3.5" />
                        <span>
                          <span className="font-bold">
                            {formatNumber(followStats.following)}
                          </span>
                          <span className="ml-1 opacity-80">Following</span>
                        </span>
                      </div>
                    </button>

                    {!isOwnProfile && (
                      <button
                        type="button"
                        onClick={handleFollowToggle}
                        disabled={followLoading || followRestricted}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold backdrop-blur-sm transition  ${
                          followLoading || followRestricted
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {followStats.isFollowing ? (
                          <>
                            <Check className="h-3.5 w-3.5" />
                            Following
                          </>
                        ) : (
                          <>
                            <Plus className="h-3.5 w-3.5" />
                            Follow
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/blog"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/60 text-white font-semibold hover:bg-white/10 transition-colors"
                >
                  Visit Community
                </Link>
              </div>
            </div>
            <div className="flex w-full flex-wrap justify-center gap-2 mt-4 lg:mt-0 lg:absolute lg:bottom-2 lg:left-1/2 lg:w-auto lg:-translate-x-1/2">
              {summaryStats.map((stat) => (
                <div
                  key={stat.label}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold backdrop-blur-sm ${stat.accent}`}
                >
                  <div
                    className={`flex items-center justify-center`}
                  >
                    {React.cloneElement(stat.icon, {
                      className: "h-3.5 w-3.5 text-white",
                    })}
                  </div>
                  <div className="inline-flex items-baseline gap-1">
                    <span className="font-bold text-white">
                      {formatNumber(stat.value)}
                    </span>
                    <span className="text-white/80">{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <Card className="p-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-200 text-sm font-medium">
                    Profile Overview
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    About {profile.name || profile.username}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">
                    {profile.bio || "This member hasn't added a bio yet."}
                  </p>
                </div>
              </div>

              {skills.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Core Expertise
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill) => (
                      <Badge
                        key={skill}
                        color="blue"
                        className="px-4 py-1.5 text-sm shadow-sm"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    Community Footprint
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    A snapshot of the content and experiences curated for the
                    community.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {contributionSections.map((section) => (
                  <div
                    key={section.title}
                    className={`relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900`}
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${section.tone} pointer-events-none`}
                    />
                    <div className="relative p-6 space-y-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {section.title}
                          </h4>
                          <span className="inline-flex items-center rounded-full bg-gray-900/10 dark:bg-gray-100/10 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-200">
                            {formatNumber(section.count || 0)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {section.hint}
                        </p>
                      </div>
                      {section.items && section.items.length > 0 ? (
                        <div className="space-y-3">
                          {section.items.slice(0, 3).map((item) => {
                            const link = section.linkFor(item);
                            return (
                              <div
                                key={item.id || item.slug || item.title}
                                className="p-4 rounded-xl bg-white/80 dark:bg-gray-900/70 border border-gray-100 dark:border-gray-800 flex items-start justify-between gap-4"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">
                                    {item.title || item.name || item.slug}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {formatDate(item.date || item.created_at)}
                                  </p>
                                </div>
                                {link && (
                                  <Link
                                    to={
                                      link.startsWith("/blog/")
                                        ? {
                                            pathname: link,
                                            state: { fromPublic: true },
                                          }
                                        : link
                                    }
                                    className="flex-shrink-0 text-indigo-600 dark:text-indigo-400 text-xs font-semibold hover:underline"
                                  >
                                    View
                                  </Link>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {section.empty}
                        </p>
                      )}
                      {section.count > 0 && (
                        <div className="pt-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              handleContributionDrawerOpen(section)
                            }
                            className="text-xs font-semibold text-indigo-600 transition hover:text-indigo-500 dark:text-indigo-300 dark:hover:text-indigo-200"
                          >
                            See all
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-10">
            <Card className="p-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-5">
                Activity Timeline
              </h3>
              <div className="space-y-5">
                {activityFeed.length > 0 ? (
                  activityFeed.map((item, index) => {
                    const link = resolveActivityLink(item);
                    const content = (
                      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm transition-colors hover:border-indigo-400 dark:hover:border-indigo-500">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {stripHtml(item.title)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {stripHtml(
                            item.detail || item.summary || item.date || "Recently"
                          )}
                        </p>
                      </div>
                    );

                    return (
                      <div
                        key={`${item.id || index}`}
                        className="relative pl-10"
                      >
                        <span className="absolute left-1 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 font-semibold">
                          {item.type?.[0]?.toUpperCase() || "C"}
                        </span>
                        {link ? (
                          <Link
                            to={
                              link.startsWith("/blog/")
                                ? {
                                    pathname: link,
                                    state: { fromPublic: true },
                                  }
                                : link
                            }
                            className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-xl"
                          >
                            {content}
                          </Link>
                        ) : (
                          content
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No recent public activity to display.
                  </p>
                )}
              </div>
            </Card>

         
          </div>
        </section>
      </div>
      <Drawer
        title={activeContributionMeta?.title || "Contributions"}
        placement="right"
        width={560}
        open={contributionDrawerOpen}
        onClose={handleContributionDrawerClose}
      >
        {activeContributionMeta?.hint && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {activeContributionMeta.hint}
          </p>
        )}
        {contributionLoading ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            Loading contributions...
          </div>
        ) : contributionItems.length ? (
          <div className="mt-4 space-y-4">
            {contributionItems.map((item) => {
              const card = renderContributionCard(item);
              if (!card) {
                return null;
              }
              const link = resolveContributionLink(
                activeContributionMeta?.type,
                item
              );
              const key = `${activeContributionMeta?.type || "item"}-${
                item.id || item.slug || item.title
              }`;
              if (typeof link === "string" && /^https?:/i.test(link)) {
                return (
                  <a
                    key={key}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    {card}
                  </a>
                );
              }
              const target = buildLinkTarget(link);
              if (target) {
                return (
                  <Link
                    key={key}
                    to={target}
                    className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    {card}
                  </Link>
                );
              }
              return <div key={key}>{card}</div>;
            })}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            {activeContributionMeta?.empty || "No contributions yet."}
          </div>
        )}
        {contributionTotal > CONTRIBUTION_PAGE_SIZE && (
          <div className="mt-6 flex justify-center">
            <Pagination
              current={contributionPage}
              pageSize={CONTRIBUTION_PAGE_SIZE}
              total={contributionTotal}
              onChange={(page) => setContributionPage(page)}
            />
          </div>
        )}
      </Drawer>
      <Drawer
        title="Followers"
        placement="right"
        width={420}
        open={followersDrawerOpen}
        onClose={() => setFollowersDrawerOpen(false)}
      >
        {followersLoading ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            Loading followers...
          </div>
        ) : followersData.length ? (
          <div className="space-y-3">
            {followersData.map((entry) => (
              <div
                key={entry.username || entry.id}
                className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white/80 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/70"
              >
                <Avatar
                  src={entry.avatar_url}
                  name={entry.name || entry.username}
                  size={10}
                  className="border border-white"
                />
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/profile/${entry.username}`}
                    className="block truncate font-semibold text-gray-900 hover:text-indigo-600 dark:text-white"
                  >
                    {entry.name || entry.username}
                  </Link>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Following since {formatFollowedAt(entry.followed_at)}
                  </p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  @{entry.username}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            No followers yet.
          </div>
        )}
        {followersTotal > FOLLOW_PAGE_SIZE && (
          <div className="mt-6 flex justify-center">
            <Pagination
              simple
              current={followersPage}
              pageSize={FOLLOW_PAGE_SIZE}
              total={followersTotal}
              onChange={(page) => setFollowersPage(page)}
            />
          </div>
        )}
      </Drawer>
      <Drawer
        title="Following"
        placement="right"
        width={420}
        open={followingDrawerOpen}
        onClose={() => setFollowingDrawerOpen(false)}
      >
        {followingLoading ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            Loading profiles...
          </div>
        ) : followingData.length ? (
          <div className="space-y-3">
            {followingData.map((entry) => (
              <div
                key={entry.username || entry.id}
                className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white/80 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/70"
              >
                <Avatar
                  src={entry.avatar_url}
                  name={entry.name || entry.username}
                  size={10}
                  className="border border-white"
                />
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/profile/${entry.username}`}
                    className="block truncate font-semibold text-gray-900 hover:text-indigo-600 dark:text-white"
                  >
                    {entry.name || entry.username}
                  </Link>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Followed on {formatFollowedAt(entry.followed_at)}
                  </p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  @{entry.username}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            This member is not following anyone yet.
          </div>
        )}
        {followingTotal > FOLLOW_PAGE_SIZE && (
          <div className="mt-6 flex justify-center">
            <Pagination
              simple
              current={followingPage}
              pageSize={FOLLOW_PAGE_SIZE}
              total={followingTotal}
              onChange={(page) => setFollowingPage(page)}
            />
          </div>
        )}
      </Drawer>
      {loginPromptAction && (
        <LoginPrompt
          action={loginPromptAction}
          onClose={() => setLoginPromptAction(null)}
        />
      )}
    </div>
  );
}
